import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import type { ForgotPasswordFormValues, ResetPasswordFormValues } from '@/types';
import Footer from '@/components/Footer';
import LogoBigColor from '@/icons/LogoBigColor';
import LogoBigWhite from '@/icons/LogoBigWhite';
import { Button, Input, InputOtp } from '@heroui/react';
import { pageBg, wrap, darkPanel, eyebrow } from '@/components/ui/design';
import { getResetErrorMessage, isExpiredCodeMessage } from '@/utils/authMessages';
import { validateResetPassword } from '@/utils/passwordValidation';

const RESEND_COOLDOWN_SECONDS = 30;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fieldClassNames = {
    input: 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted',
    inputWrapper: 'h-12 rounded-2xl bg-grey99 px-4 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey99 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/[0.08] dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60',
};
const pillShape = '[&.transform-gpu.tap-highlight-transparent]:!rounded-full [&.transform-gpu.transform-gpu.tap-highlight-transparent]:!h-12';
const primaryPill = `no-global-button-color w-full ${pillShape} bg-grey10 text-white hover:bg-black dark:bg-white dark:text-nightBg dark:hover:bg-grey90`;
const linkAccent = 'font-semibold text-grey10 underline decoration-grey90 underline-offset-4 transition-colors hover:decoration-grey10 disabled:opacity-60 dark:text-white dark:decoration-white/30 dark:hover:decoration-white';
const headingClass = 'mt-3 text-[clamp(36px,6vw,52px)] font-bold leading-[0.95] tracking-[-0.05em] text-grey10 dark:text-white';
const subtitleClass = 'mt-4 text-base leading-relaxed text-grey40 dark:text-nightMuted';

function BrandPanel() {
    return (
        <aside className={`${darkPanel} hidden min-h-[640px] flex-col justify-between p-10 md:flex lg:p-14`}>
            <img
                src="/dancer.png"
                alt="Dancer in Persian arch"
                className="absolute inset-0 -z-20 h-full w-full object-cover object-center opacity-35"
            />
            <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]/30 dark:from-[#101010] dark:via-[#101010]/80 dark:to-[#101010]/30" />
            <div className="pointer-events-none absolute -right-32 -top-32 -z-10 h-[420px] w-[420px] rounded-full bg-nightAccent/30 blur-[100px] max-md:hidden" />
            <div className="pointer-events-none absolute -bottom-40 left-1/4 -z-10 h-[360px] w-[360px] rounded-full bg-[#6d4aff]/25 blur-[100px] max-md:hidden" />
            <LogoBigWhite height={44} width={180} />
            <div>
                <p className="text-sm font-bold uppercase tracking-[0.18em] text-nightAccent">Account recovery</p>
                <h2 className="mt-4 text-[clamp(44px,5vw,72px)] font-bold leading-[0.95] tracking-[-0.05em]">
                    Find your next <span className="text-nightAccent">night out.</span>
                </h2>
                <p className="mt-6 max-w-sm text-lg leading-relaxed text-white/60">
                    Reset your password and get straight back to your tickets.
                </p>
            </div>
        </aside>
    );
}

export default function ForgotPassword() {
    const [form, setForm] = useState<ForgotPasswordFormValues>({ email: '' });
    const [resetForm, setResetForm] = useState<ResetPasswordFormValues>({ resetCode: '', password: '', confirmPassword: '' });
    const [step, setStep] = useState<'email' | 'reset'>('email');
    const [sent, setSent] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [passwordValidation, setPasswordValidation] = useState({
        minLength: false,
        hasNumber: false,
        hasLetter: false,
        isValid: false
    });
    const [passwordsMatch, setPasswordsMatch] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [cooldown]);

    const forgotPasswordMutation = useMutation({
        mutationFn: async (values: ForgotPasswordFormValues) => {
            return api.post('/auth/forgot-password', values);
        },
        onSuccess: () => {
            setSent(true);
            setStep('reset');
            setCooldown(RESEND_COOLDOWN_SECONDS);
            setMessage({ type: 'success', text: 'If an account exists for this email, we sent a verification code. Please check your email.' });
        },
        onError: (error: any) => {
            setMessage({ type: 'error', text: getResetErrorMessage(error, 'Failed to send reset code. Please try again.') });
        },
    });

    const resetPasswordMutation = useMutation({
        mutationFn: async (values: ResetPasswordFormValues) => {
            return api.post('/auth/confirm-password', {
                email: form.email,
                verificationCode: values.resetCode,
                newPassword: values.password
            });
        },
        onSuccess: () => {
            navigate('/login', { replace: true, state: { passwordReset: true } });
        },
        onError: (error: any) => {
            const errorMessage = getResetErrorMessage(error, 'Failed to reset password. Please try again.');
            if (isExpiredCodeMessage(errorMessage)) {
                setResetForm((f) => ({ ...f, resetCode: '' }));
            }
            setMessage({ type: 'error', text: errorMessage });
        },
    });

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    }

    function validatePassword(password: string) {
        setPasswordValidation(validateResetPassword(password));
    }

    function handleResetChange(e: React.ChangeEvent<HTMLInputElement>) {
        const newValue = e.target.value;
        const fieldName = e.target.name;

        setResetForm((f) => {
            const updatedForm = { ...f, [fieldName]: newValue };

            // Validate password in real-time
            if (fieldName === 'password') {
                validatePassword(newValue);
            }

            // Check if passwords match (works for both password and confirmPassword changes)
            setPasswordsMatch(updatedForm.password === updatedForm.confirmPassword && updatedForm.password !== '');

            return updatedForm;
        });
    }
    function handleResetCodeChange(value: string) {

        setResetForm((f) => ({ ...f, ['resetCode']: value }));
    }

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (forgotPasswordMutation.isPending) return;
        setMessage(null);
        if (!EMAIL_PATTERN.test(form.email.trim())) {
            setMessage({ type: 'error', text: 'Please enter a valid email address.' });
            return;
        }
        forgotPasswordMutation.mutate({ ...form, email: form.email.trim() });
    }

    function handleResetSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (resetPasswordMutation.isPending) return;
        setMessage(null);

        // Validate reset code
        if (resetForm.resetCode.length !== 6 || !/^\d{6}$/.test(resetForm.resetCode)) {
            setMessage({ type: 'error', text: 'Please enter a valid 6-digit code' });
            return;
        }

        // Validate password requirements
        if (!passwordValidation.isValid) {
            setMessage({ type: 'error', text: 'Password does not meet requirements' });
            return;
        }

        if (resetForm.password !== resetForm.confirmPassword) {
            setMessage({ type: 'error', text: 'Passwords do not match' });
            return;
        }

        resetPasswordMutation.mutate(resetForm);
    }



    function handleResend() {
        if (cooldown > 0 || forgotPasswordMutation.isPending) return;
        setMessage(null);
        setResetForm((f) => ({ ...f, resetCode: '' }));
        forgotPasswordMutation.mutate(form);
    }

    function handleBackToEmail() {
        navigate('/login');
    }

    return (
        <div className={`min-h-screen flex flex-col overflow-hidden ${pageBg}`}>
            <main className={`${wrap} grid flex-1 gap-10 py-8 md:grid-cols-2 md:py-10 lg:gap-16`}>
                <BrandPanel />
                <section className="flex items-center justify-center">
                    <div className="no-global-input flex w-full max-w-[420px] flex-col gap-6 py-4 md:py-10">
                        <div className="md:hidden">
                            <LogoBigColor width={168} height={40} className="dark:hidden" />
                            <LogoBigWhite width={168} height={40} className="hidden dark:block" />
                        </div>
                        {step === 'email' ? (
                            <>
                                <div>
                                    <p className={eyebrow}>Password reset</p>
                                    <h1 className={headingClass}>Forget Password?</h1>
                                    <p className={subtitleClass}>No worries, we’ll send a reset code to change it.</p>
                                </div>
                                <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full">
                                    <Input
                                        name="email"
                                        type="email"
                                        autoComplete="off"
                                        placeholder="Enter your email"
                                        value={form.email}
                                        onChange={handleChange}
                                        required
                                        classNames={fieldClassNames}
                                    />

                                    {message && (
                                        <div className={`p-4 rounded-2xl ring-1 text-sm ${message.type === 'success'
                                            ? 'bg-green-50 dark:bg-white/[0.04] text-green-800 dark:text-nightAccent ring-green-200 dark:ring-white/10'
                                            : 'bg-red-50 dark:bg-white/[0.04] text-red-800 dark:text-red-400 ring-red-200 dark:ring-white/10'
                                            }`}>
                                            {message.text}
                                        </div>
                                    )}
                                    <Button
                                        type="submit"
                                        color="primary"
                                        className={`mt-2 ${primaryPill}`}
                                        isDisabled={forgotPasswordMutation.isPending}
                                        isLoading={forgotPasswordMutation.isPending}
                                    >
                                        {'Send Reset Code'}
                                    </Button>
                                </form>
                                <div className="text-center">
                                    <a href="/login" className={`text-sm ${linkAccent}`}>Back to login</a>
                                </div>
                            </>
                        ) : (
                            <>
                                <div>
                                    <p className={eyebrow}>Password reset</p>
                                    <h1 className={headingClass}>Check your email</h1>
                                    <p className={subtitleClass}>
                                        If an account exists for <span className="font-semibold text-grey10 dark:text-white break-all">{form.email}</span>, we sent a 6-digit password reset code.</p>
                                </div>
                                <form onSubmit={handleResetSubmit} className="flex flex-col gap-4 w-full">

                                    <InputOtp length={6} classNames={{
                                        base: "w-full",
                                        segmentWrapper: "w-full justify-between gap-2 pb-0",
                                        segment: "h-12 w-11 sm:w-12 rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] text-lg font-bold text-grey10 data-[active=true]:bg-grey99 data-[active=true]:ring-2 data-[active=true]:ring-primary/50 dark:data-[active=true]:bg-white/[0.08] dark:bg-white/[0.06] dark:ring-white/10 dark:text-white dark:data-[active=true]:ring-nightAccent/60",
                                    }} value={resetForm.resetCode} onValueChange={handleResetCodeChange} />

                                    {/*  <Input
                                        placeholder="Code"
                                        name="resetCode"
                                        type="text"
                                        classNames={{
                                            input: "h-[48px] bg-greyInput rounded text-grey40 text-sm font-normal font-inter",
                                            inputWrapper: "h-[48px]",
                                        }}
                                        autoComplete="off"
                                        value={resetForm.resetCode}
                                        onChange={handleResetChange}
                                        required
                                        isInvalid={resetForm.resetCode === ''}

                                    /> */}
                                    <div className="relative">
                                        <Input
                                            name="password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="New Password"
                                            classNames={fieldClassNames}
                                            autoComplete="off"
                                            value={resetForm.password}
                                            onChange={handleResetChange}
                                            required

                                        />
                                        <button
                                            type="button"
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-grey40 hover:text-grey10 dark:text-nightMuted dark:hover:text-white"
                                            onClick={() => setShowPassword((v) => !v)}
                                            tabIndex={-1}
                                            aria-label="Toggle password visibility"
                                        >
                                            {showPassword ? (
                                                <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M12 5c-7 0-9 7-9 7s2 7 9 7 9-7 9-7-2-7-9-7zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10z" fill="currentColor" /></svg>
                                            ) : (
                                                <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M1 1l22 22M12 5c-7 0-9 7-9 7a16.9 16.9 0 0 0 4.2 5.3M17.6 17.6A16.9 16.9 0 0 0 21 12s-2-7-9-7a16.9 16.9 0 0 0-5.3 4.2" stroke="currentColor" strokeWidth="2" /></svg>
                                            )}
                                        </button>
                                    </div>

                                    {/* Password Validation */}
                                    {resetForm.password && (
                                        <div className="p-4 rounded-2xl bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]">
                                            <div className="text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted mb-3">Password Requirements:</div>
                                            <div className="space-y-1">
                                                <div className={`flex items-center gap-2 text-sm ${passwordValidation.minLength ? 'text-green-600' : 'text-red-500'}`}>
                                                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidation.minLength ? 'bg-green-100' : 'bg-red-100'}`}>
                                                        {passwordValidation.minLength ? (
                                                            <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    At least 8 characters
                                                </div>
                                                <div className={`flex items-center gap-2 text-sm ${passwordValidation.hasNumber ? 'text-green-600' : 'text-red-500'}`}>
                                                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidation.hasNumber ? 'bg-green-100' : 'bg-red-100'}`}>
                                                        {passwordValidation.hasNumber ? (
                                                            <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    Contains a number or symbol
                                                </div>
                                                <div className={`flex items-center gap-2 text-sm ${passwordValidation.hasLetter ? 'text-green-600' : 'text-red-500'}`}>
                                                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidation.hasLetter ? 'bg-green-100' : 'bg-red-100'}`}>
                                                        {passwordValidation.hasLetter ? (
                                                            <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    Contains uppercase and lowercase letters
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="relative">
                                        <Input
                                            classNames={fieldClassNames}
                                            name="confirmPassword"
                                            type="password"
                                            placeholder="Confirm New Password"
                                            autoComplete="off"
                                            value={resetForm.confirmPassword}
                                            onChange={handleResetChange}
                                            required
                                            isInvalid={resetForm.confirmPassword !== '' && !passwordsMatch}
                                        />
                                        {resetForm.confirmPassword && (
                                            <div className={`mt-2 px-1 text-sm ${passwordsMatch ? 'text-green-600' : 'text-red-500'}`}>
                                                {passwordsMatch ? (
                                                    <div className="flex items-center gap-1">
                                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                        Passwords match
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-1">
                                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                        </svg>
                                                        Passwords do not match
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {message && (
                                        <div className={`p-4 rounded-2xl ring-1 text-sm ${message.type === 'success'
                                            ? 'bg-green-50 dark:bg-white/[0.04] text-green-800 dark:text-nightAccent ring-green-200 dark:ring-white/10'
                                            : 'bg-red-50 dark:bg-white/[0.04] text-red-800 dark:text-red-400 ring-red-200 dark:ring-white/10'
                                            }`}>
                                            {message.text}
                                        </div>
                                    )}
                                    <Button
                                        type="submit"
                                        color="primary"
                                        className={`mt-2 ${primaryPill}`}
                                        isDisabled={resetPasswordMutation.isPending || resetForm.resetCode.length !== 6 || !passwordValidation.isValid || (resetForm.confirmPassword !== '' && !passwordsMatch)}
                                        isLoading={resetPasswordMutation.isPending}
                                    >
                                        {'Reset Password'}
                                    </Button>
                                </form>
                                <div className="flex flex-col gap-3">
                                    <div className="text-center text-sm text-grey40 dark:text-nightMuted">
                                        Didn&apos;t receive the code?{' '}
                                        <button
                                            type="button"
                                            className={`text-sm ${linkAccent}`}
                                            onClick={handleResend}
                                            disabled={cooldown > 0 || forgotPasswordMutation.isPending}
                                        >
                                            {forgotPasswordMutation.isPending ? 'Sending...' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Click to resend'}
                                        </button>
                                    </div>
                                    <div className="text-center">
                                        <button
                                            onClick={handleBackToEmail}
                                            className={`text-sm ${linkAccent}`}
                                        >
                                            Back to login
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </section>
            </main>
            {/* Footer (Full Width) */}
            <Footer />
        </div>
    );
} 